import { type ProjectWithDetails } from "@/hooks/useProjects";
import { type ReportAggregation } from "@glorify-llc/accounting-core";
import { buildReportSummaryItem } from "@/lib/metrics/metric-registry";

export type ProjectStatusOverviewContext = {
  asOf: Date;
  projects: ProjectWithDetails[];
};

/**
 * Normalize a free-form status to canonical lowercase + underscore form so
 * `"Active"`, `" ACTIVE "`, `"In Progress"`, and `"in_progress"` collapse to
 * the same enum-style key. The DB enum is lowercase
 * (`planning|active|on_hold|completed|cancelled`) but defensive coercion
 * protects against legacy rows, imported data, or future schema drift.
 */
const normalizeStatus = (status: unknown): string => {
  if (typeof status !== "string") return "";
  return status.trim().toLowerCase().replace(/\s+/g, "_");
};

const isCompletedTaskStatus = (status: unknown): boolean => {
  const normalized = normalizeStatus(status);
  return normalized === "completed" || normalized === "complete" || normalized === "done";
};

export function buildProjectStatusOverviewAggregation({
  asOf,
  projects,
}: ProjectStatusOverviewContext): ReportAggregation {
  const safeProjects = Array.isArray(projects) ? projects : [];

  const rows = safeProjects
    .map((project) => {
      const tasks = Array.isArray(project?.tasks) ? project.tasks : [];
      const totalTasks = tasks.length;
      const completedTasks = tasks.filter(
        (t) => isCompletedTaskStatus(t?.status) || Boolean(t?.completed_at),
      ).length;

      // Guard against divide-by-zero on totalTasks=0 (null instead of NaN /
      // "0%"). Floor below 100 so a 99.4% project doesn't claim "100%
      // complete" while still leaving the genuine all-done case at 100.
      let completion: number | null = null;
      if (totalTasks > 0) {
        const ratio = completedTasks / totalTasks;
        if (Number.isFinite(ratio)) {
          completion = ratio >= 1 ? 100 : Math.floor(ratio * 100);
        }
      }

      const rawName = typeof project?.name === "string" ? project.name.trim() : "";

      return {
        id: project.id,
        label: rawName || "Project",
        status: project?.status ?? null,
        completion: completion !== null ? `${completion}%` : null,
        completionPercent: completion,
        totalTasks,
        completedTasks,
        startDate: project?.start_date ?? null,
        endDate: project?.end_date ?? null,
        entityType: "project" as const,
        drilldownUrl: `/projects/${project.id}`,
      };
    })
    // Stable, deterministic order: by status priority (active > planning >
    // on_hold > completed > cancelled > unknown), then label, then id. The
    // DB returns by created_at desc which gives caller-visible churn on each
    // refetch as new projects come in.
    .sort((a, b) => {
      const sa = STATUS_SORT_RANK[normalizeStatus(a.status)] ?? STATUS_SORT_RANK.__unknown;
      const sb = STATUS_SORT_RANK[normalizeStatus(b.status)] ?? STATUS_SORT_RANK.__unknown;
      if (sa !== sb) return sa - sb;
      const la = a.label.toLowerCase();
      const lb = b.label.toLowerCase();
      if (la < lb) return -1;
      if (la > lb) return 1;
      if (a.id < b.id) return -1;
      if (a.id > b.id) return 1;
      return 0;
    });

  const activeCount = rows.filter((row) => normalizeStatus(row.status) === "active").length;

  return {
    asOf,
    summary: [
      buildReportSummaryItem("report.project_status.project_count", rows.length),
      buildReportSummaryItem("report.project_status.active_count", activeCount),
    ],
    rows,
  };
}

const STATUS_SORT_RANK: Record<string, number> = {
  active: 0,
  planning: 1,
  on_hold: 2,
  completed: 3,
  cancelled: 4,
  __unknown: 5,
};
